import { connect } from 'react-redux';
import get from 'lodash/get';

import ScriptConfigContainer from './ScriptConfigContainer';

// constants
import {
  applicationProperties,
  applicationTypes,
  scriptTypes
} from 'constants/scripts';
import { inputStates, selectIdPrefix } from 'constants/values';

// selectors
import { isVolumeTypeSwitchEnabled } from 'user/selectors';
import {
  getActiveScript,
  getActiveScriptId,
  getActiveScriptConfigView,
  getActiveSubstanceTypeUuid,
  hasNitrapyrin as hasNitrapyrinFunc,
  usingLiquidIsChecked as usingLiquidIsCheckedFunc,
  getNitrogenApplicationRateType
} from 'current/selectors';
import {
  getSubstanceTypesForApplicationType,
  substanceTypeIsNitrogen as substanceTypeIsNitrogenFunc
} from 'products/fertility-selectors';
import { getNitrapyrin } from 'products/selectors';
import {
  getApplicationTypeForScriptType,
  getDisplayScriptName,
  getScriptTypeForScript,
  getProductsForScript,
  getScriptById,
  scriptTypeIsNitrogen
} from 'scripts/selectors';
import {
  getRenameScriptValidationResult
} from 'validations/selectors';
import { hasProductSelected } from '../current/uiwidgets/selectors';

// actions
import {
  setIsUsingLiquid,
  setActiveScriptConfigView,
  setActiveSubstanceTypeUuid,
  setHasNitrapyrin,
  populateFertilizerProductInput,
  setNitrogenApplicationRateType,
  setFertilizerProductInput
} from 'current/uiwidgets';
import {
  clearFertilizerApplicationsForActiveScript,
  removeProductRateForZones,
  updateFertilityScriptTypeForActiveScript,
  updateProductApplicationPropertyForZones,
  updateScriptNameToSave,
  setScriptState,
  setNitrapyrin
} from 'scripts';
import {
  getSubstanceTypeUuidForFertilityScriptType 
} from 'scripts/fertility-selectors';
import { setApplicationProducts } from 'products';

// util
import isCustom from 'util/isCustom';
import { getPlantingPlanMaxDate, getPlantingPlanMinDate, getPlantingPlanParams } from 'planting-plan-form/selectors';


export default connect(
  (state) => {
    const script = getActiveScript(state);
    const scriptType = getScriptTypeForScript(script);
    const applicationType = getApplicationTypeForScriptType(scriptType);
    const products = script && getProductsForScript(script);
    const substanceTypeUuid = getActiveSubstanceTypeUuid(state);
    const plantingPlanParams = getPlantingPlanParams(state);

    return {
      applicationType: applicationType,
      displayScriptName: getDisplayScriptName(script),
      hasNitrapyrin: hasNitrapyrinFunc(state),
      maxDate: getPlantingPlanMaxDate(state),
      minDate: getPlantingPlanMinDate(state),
      nitrapyrin: scriptType === scriptTypes.FERTILITY ? getNitrapyrin(state) : '',
      plantingPlanParams: plantingPlanParams,
      products,
      scriptId: getActiveScriptId(state),
      scriptType: scriptType,
      scriptConfigView: getActiveScriptConfigView(state),
      substanceTypeUuid,
      substanceTypeIsNitrogen: substanceTypeIsNitrogenFunc(state, substanceTypeUuid),
      substanceTypes: getSubstanceTypesForApplicationType(state, applicationType),
      usingLiquidIsChecked: usingLiquidIsCheckedFunc(state),
      validationResult: getRenameScriptValidationResult(state),
      nitrogenApplicationRateType: getNitrogenApplicationRateType(state),
      showNitrogenApplicationRateTypeSwitch: 
        scriptTypeIsNitrogen(script) && !isCustom(script) && isVolumeTypeSwitchEnabled(state),
      state
    };
  },
  (dispatch) => {
    return {
      onChangeNitrapyrin: (e, scriptId, nitrapyrin) => {
        const hasNitrapyrin = e.target.checked;
        dispatch(setHasNitrapyrin({ hasNitrapyrin }));
        dispatch(setNitrapyrin({ scriptId, hasNitrapyrin, nitrapyrin }));
        dispatch(setScriptState({scriptId, scriptState: inputStates.CHANGED }));
      },
      onChangePlantingFertilizerApplication: (e) => {
        const isUsingLiquid = get(e, 'target.checked', e);
        dispatch(setIsUsingLiquid({ isUsingLiquid }));
        if (!isUsingLiquid) {
          dispatch(clearFertilizerApplicationsForActiveScript());
        }
      },
      onChangeScriptName: (e) => dispatch(updateScriptNameToSave(e.target.value)),
      onClickScriptConfigViewTrigger: (viewToShow) => dispatch(setActiveScriptConfigView(viewToShow)),

      /* The method below is coupled to some assumptions re crop protection UI behavior. Ideally, we
         would instead update onSelectIncorporationMethod() to serve all flows, but at time of writing,
         we are close to release of the feature and want to isolate the changes from regressing other
         functionality.

         Assumptions:
         - This method will be called *after* script has been created.
         - Product rate must be cleared because a change of incorporation method also changes the rate unit.
         - Incorporation method will be stored in current script zones in store as opposed to planting plan.

         TODO CFVWEB-8197:
        - Look at enhancing setActiveIncorporationMethod() to update the current script in store as well as
          planting plan params.
        - With the above or some other solution, eliminate onSelectIncorporationMethodForCropProtection() and just
          use onSelectIncorporationMethod().
         */
      onSelectIncorporationMethodForCropProtection: (value, scriptId) => {
        dispatch(removeProductRateForZones({scriptId, applicationType:applicationTypes.APPLICATION}));
        dispatch(updateProductApplicationPropertyForZones({
          scriptId, property:applicationProperties.INCORPORATION_METHOD, value
        }));
        dispatch(setScriptState({ scriptId, scriptState: inputStates.CHANGED }));
      },
      onFertilityInputMount: () => {
        dispatch(populateFertilizerProductInput({ substanceTypeUuid: null }));
      },
      onSelectSubstanceType: (substanceTypeUuid) => {
        dispatch(setActiveSubstanceTypeUuid({ substanceTypeUuid }));
        dispatch(populateFertilizerProductInput({ substanceTypeUuid }));
      },
      onSelectFertilizerProduct: (substanceTypeUuid) => {
        dispatch(updateFertilityScriptTypeForActiveScript({ substanceTypeUuid }));
      },
      resetConfigForFertilityScriptIfNeeded: (state) => ({scriptId}) => {
        if (!hasProductSelected(state)) {
          const script = getScriptById(state, scriptId);
          const products = getProductsForScript(script);
          const scriptType = getScriptTypeForScript(script);
          const applicationType = getApplicationTypeForScriptType(scriptType);
          const substanceTypeUuid = getSubstanceTypeUuidForFertilityScriptType(state, get(script, 'tag'));
          const selectId = `${selectIdPrefix}1`;
          const currentProduct = products[0];
          const currentProductId = currentProduct.id;
          const chemicalTypeUuid = currentProduct.chemicalTypeId;
          
          dispatch(setActiveSubstanceTypeUuid({substanceTypeUuid}));
          dispatch(setApplicationProducts({ applicationType, products, substanceTypeUuid, chemicalTypeUuid }));
          dispatch(setFertilizerProductInput({ currentProductId, products, selectId }));
          
        }
      },
      setNitrogenApplicationRateType: (type) => dispatch(setNitrogenApplicationRateType(type))
    };
  },
  ({state, ...stateProps}, dispatchProps) => {
    return {
      ...stateProps, 
      ...dispatchProps, 
      resetConfigForFertilityScriptIfNeeded: 
        dispatchProps.resetConfigForFertilityScriptIfNeeded(state) 
    };
  }
)(ScriptConfigContainer);
